import * as React from 'react'

import { TFCustomer, TFPaymentMethod, TFUserAgreement } from '~graphql/generated/clientGraphql'
import { objectEntries } from '~logic/helpers/objectUtils/object'
import { useUpdatedRef } from '~logic/hooks/useUpdatedRef'
import { createContext } from '~logic/lib/react'

import {
  DefaultValues,
  FormProvider as ReactHookFormProvider,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  useOnValidationError,
} from '../../../segments/Form'
import { useCountryPhonePrefix } from '../hooks/useCountryPhonePrefix'
import { useRefetchCartOnFormUpdate } from '../hooks/useRefetchCartOnFormUpdate'
import { usePersistedFormValuesCtx, usePersistedNoteCtx } from './FormPersistenceProvider'

export type TOrderForm = {
  givenName: string
  familyName: string
  phonePrefix: string
  phoneNumber: string
  email: string
  note: string
  paymentMethod: Nullable<TFPaymentMethod>
  confirmedAgreements: RoA<TFUserAgreement>
}

const DEFAULT_VALUES: DefaultValues<TOrderForm> = {
  givenName: '',
  familyName: '',
  phonePrefix: '',
  phoneNumber: '',
  email: '',
  note: '',
  paymentMethod: null,
  confirmedAgreements: [],
}

const [
  ErrorHandlerProvider, //
  useFormErrorHandlerCtx,
] = createContext<() => void>(`FormErrorHandler`)
const [
  SubmitProvider, //
  useFormSubmitCtx,
] = createContext<ReturnType<UseFormHandleSubmit<TOrderForm>>>(`FormSubmit`)

type TProps = RequiredChildren & {
  submitOrder: SubmitHandler<TOrderForm>
  customer?: Nullable<TFCustomer>
}

/**
 * Provider of the form values and submit handler.
 * Use the `useFormSubmit` to access the submit handler.
 * Use the `useFormErrorHandler` to access the error handler.
 */
export const FormProvider: React.FC<TProps> = ({ customer = null, submitOrder, children }) => {
  const persistedForm = usePersistedFormValuesCtx()
  const persistedNote = usePersistedNoteCtx()
  const refCustomer = useUpdatedRef(customer)

  // if form values or note is stored in persistence (persistedForm or persistedNote), it initialize the form with them when loaded
  const defaultValues = React.useMemo(() => {
    const persistedFormValues = persistedForm ?? DEFAULT_VALUES
    const persistedNoteValue = persistedNote ? { note: persistedNote } : persistedFormValues
    return { ...DEFAULT_VALUES, ...persistedFormValues, ...persistedNoteValue }
  }, [persistedForm, persistedNote])

  // ! initial values are only used on the first render
  // if you need to update values later, use the `setValue` method
  const methods = useForm<TOrderForm>({ defaultValues })

  const { control, setValue, getValues, formState, handleSubmit } = methods

  // if customer’s data is loaded, use it to fill the form
  // do it just once for a customer (based on their ID)
  React.useEffect(() => {
    if (!refCustomer.current) return

    const customerValues = getCustomerValues(refCustomer.current)

    objectEntries(customerValues).forEach(([key, value]) => {
      setValue(key, value)
    })
  }, [refCustomer, setValue, customer?.id])

  // refetch Cart content when the customer contact is updated
  useRefetchCartOnFormUpdate({ control })

  // pre-fill the phone prefix with code from the country that the branch is in
  useCountryPhonePrefix({ getValues, setValue })

  const onValidationError = useOnValidationError<TOrderForm>()

  // will run the `onValidationError` callback with current form errors
  const handleFormError = React.useCallback(
    () => onValidationError(formState.errors),
    [formState.errors, onValidationError],
  )

  return (
    <SubmitProvider value={handleSubmit(submitOrder, onValidationError)}>
      <ErrorHandlerProvider value={handleFormError}>
        <ReactHookFormProvider {...methods}>{children}</ReactHookFormProvider>
      </ErrorHandlerProvider>
    </SubmitProvider>
  )
}

export { useFormSubmitCtx, useFormErrorHandlerCtx }

// * HELPERS

type TCustomerValues = Pick<
  TOrderForm,
  'givenName' | 'familyName' | 'phoneNumber' | 'phonePrefix' | 'email'
>

// returns order-form related customer’s details
const getCustomerValues = (customer: TFCustomer) => {
  const customerValues: TCustomerValues = {
    givenName: customer.givenName ?? ``,
    familyName: customer.familyName ?? ``,
    phoneNumber: customer.phoneNumber,
    phonePrefix: customer.phonePrefix,
    email: customer.email,
  }

  return customerValues
}
